import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  Fab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DataTable, type DataTableColumn, type QuickFilter } from '@/components/common';
import type { PurchaseOrder, PurchaseOrderItem, PurchaseOrderStatus, Supplier, InventoryItem } from '@/types';
import { useTranslation } from 'react-i18next';
import { apiErrorCode, apiErrorReason, describeApiError } from '@/lib/describeApiError';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { PurchaseOrderDetailState } from '@/components/purchase-orders/PurchaseOrderDetailModal';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { describeDispatchError, KNOWN_ERROR_CODES } from '@/features/purchase-orders/dispatch/dispatchErrors';
import { useReceivingAccess } from '@/features/purchase-orders/receiving/receivingSession';
import { useInvoiceAccess } from '@/features/purchase-orders/invoices';
import { downloadCsv } from '@/utils/csv';
import {
  PurchaseOrderDetailModal,
  PurchaseOrderFormDialog,
  MatchInvoiceDialog,
  type PurchaseOrderFormData,
} from '@/components/purchase-orders';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useSuppliers,
  useInventoryParts,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useUpdatePurchaseOrderStatus,
  useMatchPurchaseOrderInvoice,
  useWarehouses,
  type PurchaseOrderDetail,
  type PurchaseOrderListItem,
  type PurchaseOrderLineItem,
  type SupplierListItem,
} from '@/hooks';
import { dateFnsLocale } from '@/utils/locale';
import { useUrlSearchParam, withCommittedUrlSearchParams } from '@/hooks/useUrlSearchParam';

type TabValue = 'all' | PurchaseOrderStatus;

const TAB_VALUES: readonly TabValue[] = [
  'all', 'draft', 'approved', 'ordered', 'partially_received', 'received', 'cancelled',
];

function isTabValue(value: string): value is TabValue {
  return (TAB_VALUES as readonly string[]).includes(value);
}

// Map API PO to frontend PurchaseOrder shape. `warehouseId` and real `items`
// aren't part of the list projection — populated separately from
// usePurchaseOrder(id) once a specific PO is selected (see
// `selectedPOWithDetail` below), mirroring how PurchaseOrderReceiveDialog/
// ReturnFormDialog already fetch detail before rendering lines.
/** Detail route shape, narrowed to the list projection `mapPOListItem` already understands. */
function detailToListItem(detail: PurchaseOrderDetail): PurchaseOrderListItem {
  return {
    id: detail.id,
    orderNumber: detail.orderNumber,
    supplierId: detail.supplierId,
    supplier: {
      id: detail.supplier.id,
      name: detail.supplier.name,
      deliveryDays: detail.supplier.deliveryDays,
    },
    status: detail.status,
    lineCount: detail.items.length,
    totalQuantity: detail.items.reduce((sum, item) => sum + item.quantity, 0),
    subtotalCents: detail.subtotalCents,
    taxCents: detail.taxCents,
    shippingCents: detail.shippingCents,
    totalCents: detail.totalCents,
    notes: detail.notes,
    createdByName: detail.createdByName,
    orderDate: detail.orderDate,
    expectedDeliveryDate: detail.expectedDeliveryDate,
    actualDeliveryDate: detail.actualDeliveryDate,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
  };
}

function mapPOListItem(po: PurchaseOrderListItem): PurchaseOrder {
  return {
    id: po.id,
    orderNumber: po.orderNumber,
    supplierId: po.supplierId,
    warehouseId: '',
    supplier: po.supplier as Supplier,
    status: po.status as PurchaseOrderStatus,
    items: [],
    subtotalCents: po.subtotalCents,
    taxCents: po.taxCents,
    shippingCents: po.shippingCents,
    totalCents: po.totalCents,
    notes: po.notes ?? undefined,
    createdById: '',
    createdByName: po.createdByName,
    expectedDeliveryDate: po.expectedDeliveryDate ?? undefined,
    receivedAt: po.actualDeliveryDate ?? undefined,
    // Invoice-match fields aren't part of the list projection either —
    // populated from usePurchaseOrder(id) in selectedPOWithDetail below.
    invoiceMatched: false,
    createdAt: po.createdAt,
    updatedAt: po.updatedAt,
  };
}

// Map a real PO-detail line (from usePurchaseOrder) to the frontend line shape.
function mapPOLineItem(line: PurchaseOrderLineItem): PurchaseOrderItem {
  return {
    id: line.id,
    inventoryItemId: line.partId,
    partNumber: line.partNumber,
    name: line.name,
    quantity: line.quantity,
    unitCostCents: line.unitCostCents,
    totalCostCents: line.totalCostCents,
    receivedQuantity: line.quantityReceived,
    // Additive counters: stated by the order or unknown. An absent figure is never turned into zero.
    ...(line.quantityRejected !== undefined ? { rejectedQuantity: line.quantityRejected } : {}),
    ...(line.quantityHeld !== undefined ? { heldQuantity: line.quantityHeld } : {}),
    ...(line.quantityCancelled !== undefined ? { cancelledQuantity: line.quantityCancelled } : {}),
    linkedBookingId: line.linkedBookingId,
    linkedWorkOrder: line.linkedWorkOrder,
  };
}

function mapSupplierToFrontend(s: SupplierListItem): Supplier {
  return {
    id: s.id,
    name: s.name,
    deliveryDays: s.defaultLeadTimeDays,
    phone: s.phone ?? undefined,
    email: s.email ?? undefined,
  };
}

function mapPartToInventoryItem(part: { id: string; sku: string; name: string; sellPriceCents: number; costPriceCents: number; createdAt: string; updatedAt: string }): InventoryItem {
  return {
    id: part.id,
    partNumber: part.sku,
    name: part.name,
    unitPriceCents: part.sellPriceCents,
    costPriceCents: part.costPriceCents,
    category: 'other',
    quantity: 0,
    minStockLevel: 0,
    createdAt: part.createdAt,
    updatedAt: part.updatedAt,
  };
}

const statusColors: Record<PurchaseOrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  approved: 'info',
  ordered: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'error',
};

export default function PurchaseOrdersPage() {
  const notify = useNotify();
  const isMobile = useIsMobile();
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const { t: tDispatch } = useTranslation('procurementDispatch');
  const canCreatePurchaseOrder = useAuthStore((s) => s.hasPermission('purchase_orders.create'));
  const tenantSettings = useTenantSettings();
  // Receiving, sending and supplier invoices need the workshop's parts ordering module. Not known (not read yet or the read
  // failed) is not "on": the sections wait for a definite answer. The server stays the authority on every action.
  const procurementEnabled = tenantSettings.isSuccess && tenantSettings.partsOrderingEnabled;
  const receivingAccess = useReceivingAccess();
  const invoiceAccess = useInvoiceAccess();
  const canReceiveGoods = procurementEnabled && receivingAccess.canReceive;
  const canOpenInvoices = procurementEnabled && (invoiceAccess.canRead || receivingAccess.canReceive);
  // Whose page this is. A different tenant, user, login or session generation starts with nothing selected or open.
  const sessionOwner = useAuthStore((s) =>
    JSON.stringify([s.user?.tenantId ?? null, s.user?.id ?? null, s.sessionFamilyId, s.sessionEpoch, s.isAuthenticated]),
  );
  const [selectionOwner, setSelectionOwner] = useState(sessionOwner);

  const statusLabels: Record<PurchaseOrderStatus, string> = {
    draft: t('purchaseOrders.status.draft'),
    approved: t('purchaseOrders.status.approved'),
    ordered: t('purchaseOrders.status.ordered'),
    partially_received: t('purchaseOrders.status.partiallyReceived'),
    received: t('purchaseOrders.status.received'),
    cancelled: t('purchaseOrders.status.cancelled'),
  };

  // Fetch purchase orders from API
  const poQuery = usePurchaseOrders();
  const purchaseOrders = useMemo(() => {
    if (!poQuery.data?.pages) return [];
    return poQuery.data.pages.flatMap((page) => page.data.map(mapPOListItem));
  }, [poQuery.data]);

  // Fetch suppliers from API
  const suppliersQuery = useSuppliers();
  const suppliersList: Supplier[] = useMemo(() => {
    if (!suppliersQuery.data?.pages) return [];
    return suppliersQuery.data.pages.flatMap((page) => page.data.map(mapSupplierToFrontend));
  }, [suppliersQuery.data]);

  // Fetch inventory parts for form dialog
  const partsQuery = useInventoryParts();
  const inventory: InventoryItem[] = useMemo(() => {
    if (!partsQuery.data?.pages) return [];
    return partsQuery.data.pages.flatMap((page) => page.data.map(mapPartToInventoryItem));
  }, [partsQuery.data]);

  // Warehouses for the create/edit form's destination picker
  const warehousesQuery = useWarehouses();
  const warehousesList = warehousesQuery.data?.data ?? [];

  // Mutations
  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  const updateStatusMutation = useUpdatePurchaseOrderStatus();
  const matchInvoiceMutation = useMatchPurchaseOrderInvoice();
  // Status tab and search live in the URL so Back restores them (#4100).
  // Prefixed: this page is a tab of the inventory page, which owns `q` and
  // `filter`.
  const [tabParam, setTabParam] = useUrlSearchParam('poStatus', { immediate: true });
  const tab: TabValue = isTabValue(tabParam) ? tabParam : 'all';
  const setTab = (next: TabValue) => setTabParam(next === 'all' ? '' : next);
  const [searchQuery, setSearchQuery] = useUrlSearchParam('poQ');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const poParam = searchParams.get('po');
  /** Last `po` the deep-link effect applied. Removal is a transition (close, Back), not the initial empty URL. */
  const poParamApplied = useRef<string | null>(null);
  /** Ignore this id until `po` has left the URL, so a late detail read cannot reopen a closed order. */
  const closedPoId = useRef<string | null>(null);
  /** The edit form and the invoice match still need the order after `po` leaves the URL. */
  const keepPoSelection = useRef(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [matchInvoiceDialogOpen, setMatchInvoiceDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPO, setMenuPO] = useState<PurchaseOrder | null>(null);
  /** Purchase order awaiting cancel confirmation (#2722). */
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);

  if (selectionOwner !== sessionOwner) {
    // Another session or tenant: nothing of the previous one stays selected or open (adjusted while rendering, so no frame
    // shows the previous order).
    setSelectionOwner(sessionOwner);
    setSelectedPO(null);
    setMenuPO(null);
    setAnchorEl(null);
    setCancelTarget(null);
    setDetailModalOpen(false);
    setFormDialogOpen(false);
    setMatchInvoiceDialogOpen(false);
    closedPoId.current = poParam;
    keepPoSelection.current = false;
  }

  // Real line-level detail (items, warehouseId) for whichever PO is selected —
  // the list projection never carries these (see mapPOListItem above). A `?po=`
  // link starts the same read before the row is selected, including an order
  // that is not on the loaded pages.
  const poDetailQuery = usePurchaseOrder(selectedPO?.id ?? poParam ?? undefined);
  const listedPo = useMemo(
    () => (poParam ? purchaseOrders.find((item) => item.id === poParam) ?? null : null),
    [purchaseOrders, poParam],
  );

  // `?po=` opens the detail once the order is known. A plain click writes the
  // same param with replace and leaves poStatus / poQ in place (#4108).
  useEffect(() => {
    const previous = poParamApplied.current;
    poParamApplied.current = poParam;
    if (formDialogOpen || matchInvoiceDialogOpen) return;
    if (!poParam) {
      closedPoId.current = null;
      if (previous && !keepPoSelection.current) {
        setDetailModalOpen(false);
        setSelectedPO(null);
      } else if (previous) {
        setDetailModalOpen(false);
      }
      return;
    }
    if (closedPoId.current === poParam) return;
    keepPoSelection.current = false;
    const fromDetail = poDetailQuery.data && poDetailQuery.data.id === poParam
      ? mapPOListItem(detailToListItem(poDetailQuery.data))
      : null;
    const po = listedPo ?? fromDetail;
    if (!po) return;
    setSelectedPO((current) =>
      current && current.id === po.id && current.updatedAt === po.updatedAt ? current : po,
    );
    setDetailModalOpen(true);
  }, [poParam, listedPo, poDetailQuery.data, formDialogOpen, matchInvoiceDialogOpen]);
  // The edit form must not open on stale (items-less) data — it would
  // initialize empty and then wipe any in-progress user edits once detail
  // arrives and the form's init effect re-runs (same race as #724's part-edit
  // fix). The read-only detail modal has no such risk and isn't gated.
  const detailData = selectedPO && poDetailQuery.data?.id === selectedPO.id ? poDetailQuery.data : null;
  const isFormDataReady = !selectedPO || detailData !== null;
  // The order on screen is the server's own, refetched order, not the list row it was opened from: its status, totals, revision
  // (`updatedAt`) and line counters all come from the detail, so a confirmed receipt, hold decision or close-short shows at once.
  // The list row only names the order until the detail has arrived (then `detailState` is not `ready`).
  const selectedPOWithDetail = useMemo<PurchaseOrder | null>(() => {
    if (!selectedPO) return null;
    if (detailData) {
      // Fields the detail owns are taken from it alone, also when it states none: a note, date or delivery the order no longer
      // has must not survive from the list row it was opened from.
      const { notes: _rowNotes, expectedDeliveryDate: _rowExpected, receivedAt: _rowReceived, ...row } = selectedPO;
      return {
        ...row,
        ...(detailData.notes !== null ? { notes: detailData.notes } : {}),
        ...(detailData.expectedDeliveryDate !== null ? { expectedDeliveryDate: detailData.expectedDeliveryDate } : {}),
        ...(detailData.actualDeliveryDate !== null ? { receivedAt: detailData.actualDeliveryDate } : {}),
        supplierId: detailData.supplierId,
        supplier: {
          id: detailData.supplier.id,
          name: detailData.supplier.name,
          deliveryDays: detailData.supplier.deliveryDays,
          ...(detailData.supplier.email !== null ? { email: detailData.supplier.email } : {}),
          ...(detailData.supplier.phone !== null ? { phone: detailData.supplier.phone } : {}),
        },
        status: detailData.status as PurchaseOrderStatus,
        warehouseId: detailData.warehouseId,
        items: detailData.items.map(mapPOLineItem),
        subtotalCents: detailData.subtotalCents,
        taxCents: detailData.taxCents,
        shippingCents: detailData.shippingCents,
        totalCents: detailData.totalCents,
        createdByName: detailData.createdByName,
        approvedByName: detailData.approvedByName,
        approvedAt: detailData.approvedAt,
        supplierInvoiceNumber: detailData.supplierInvoiceNumber,
        supplierInvoiceDate: detailData.supplierInvoiceDate,
        supplierInvoiceTotalCents: detailData.supplierInvoiceTotalCents,
        invoiceMatched: detailData.invoiceMatched,
        invoiceDiscrepancyCents: detailData.invoiceDiscrepancyCents,
        invoiceMatchedAt: detailData.invoiceMatchedAt,
        invoiceMatchedByName: detailData.invoiceMatchedByName,
        ...(detailData.invoiceMatchLevel !== undefined ? { invoiceMatchLevel: detailData.invoiceMatchLevel } : {}),
        updatedAt: detailData.updatedAt,
      };
    }
    return selectedPO;
  }, [selectedPO, detailData]);
  const detailState: PurchaseOrderDetailState = detailData
    ? 'ready'
    : poDetailQuery.isError
      ? 'error'
      : 'loading';

  // Filter and search
  const filteredOrders = useMemo(() => {
    let filtered = purchaseOrders;

    // Filter by tab
    if (tab !== 'all') {
      filtered = filtered.filter((po) => po.status === tab);
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.orderNumber.toLowerCase().includes(query) ||
          po.supplier.name.toLowerCase().includes(query) ||
          po.items.some((item) => item.partNumber.toLowerCase().includes(query) || item.name.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [purchaseOrders, tab, searchQuery]);

  // Count by status
  const statusCounts = useMemo(() => {
    const counts: Record<TabValue, number> = {
      all: purchaseOrders.length,
      draft: 0,
      approved: 0,
      ordered: 0,
      partially_received: 0,
      received: 0,
      cancelled: 0,
    };

    purchaseOrders.forEach((po) => {
      counts[po.status]++;
    });

    return counts;
  }, [purchaseOrders]);

  const quickFilters: QuickFilter[] = [
    { label: `${t('purchaseOrders.tabAll')} (${statusCounts.all})`, value: 'all' },
    { label: `${t('purchaseOrders.tabDrafts')} (${statusCounts.draft})`, value: 'draft' },
    { label: `${t('purchaseOrders.tabApproved')} (${statusCounts.approved})`, value: 'approved' },
    { label: `${t('purchaseOrders.tabOrdered')} (${statusCounts.ordered})`, value: 'ordered' },
    { label: `${t('purchaseOrders.tabPartiallyReceived')} (${statusCounts.partially_received})`, value: 'partially_received' },
    { label: `${t('purchaseOrders.tabReceived')} (${statusCounts.received})`, value: 'received' },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, po: PurchaseOrder) => {
    setAnchorEl(event.currentTarget);
    setMenuPO(po);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPO(null);
  };

  const writePoParam = (id: string | null) => {
    const next = new URLSearchParams(withCommittedUrlSearchParams(searchParams));
    if (id) next.set('po', id);
    else next.delete('po');
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  };

  const purchaseOrderHref = (po: PurchaseOrder) => {
    const next = new URLSearchParams(withCommittedUrlSearchParams(searchParams));
    next.set('po', po.id);
    return `${location.pathname}?${next.toString()}`;
  };

  const handleView = (po: PurchaseOrder) => {
    closedPoId.current = null;
    keepPoSelection.current = false;
    setSelectedPO(po);
    setDetailModalOpen(true);
    handleMenuClose();
    writePoParam(po.id);
  };

  const handleEdit = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setFormDialogOpen(true);
    handleMenuClose();
  };

  const handleCreate = () => {
    setSelectedPO(null);
    setFormDialogOpen(true);
  };

  const handleApprove = (po: PurchaseOrder) => {
    updateStatusMutation.mutate(
      { id: po.id, status: 'approved' },
      {
        onSuccess: () => notify.success(t('purchaseOrders.approveSuccess'), `${po.orderNumber} ${t('purchaseOrders.status.approved').toLowerCase()}`),
        onError: () => notify.error(tc('error'), t('purchaseOrders.approveFailed')),
      },
    );
    handleMenuClose();
  };

  /**
   * The manual `approved -> ordered` transition. It records that the order was placed by hand: it sends nothing and does not say
   * so. The server refuses it while a sending is queued, in progress or of unknown outcome (`dispatch_already_open`); that refusal
   * is shown in words, so the user looks at the sending instead of retrying.
   */
  const handleMarkOrdered = (po: PurchaseOrder) => {
    updateStatusMutation.mutate(
      { id: po.id, status: 'ordered' },
      {
        onSuccess: () => {
          notify.success(t('purchaseOrders.markedOrdered'), t('purchaseOrders.markedOrderedDesc', { orderNumber: po.orderNumber }));
        },
        onError: (error) => {
          const code = apiErrorCode(error);
          notify.error(
            tc('error'),
            code !== null && KNOWN_ERROR_CODES.includes(code)
              ? describeDispatchError(tDispatch, error)
              : t('purchaseOrders.statusUpdateFailed'),
          );
        },
      },
    );
    handleMenuClose();
  };

  /**
   * Receiving goods has one entry: the order's receiving workbench in its detail, which asks for the quality check, the arrived /
   * accepted / rejected / held split and the held-unit decisions, and closes an order short. The older receive action, which had
   * none of that, is gone from the page.
   */
  const handleOpenReceiving = (po: PurchaseOrder) => {
    handleView(po);
  };

  const handleMatchInvoice = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setMatchInvoiceDialogOpen(true);
    handleMenuClose();
  };

  /** The header match is refused (409) once the per-line flow owns the order: say so instead of a generic failure. */
  const matchInvoiceFailureText = (error: unknown): string =>
    apiErrorReason(error) === 'legacy_header_match_refused_for_line_flow'
      ? t('purchaseOrderDetail.invoiceMatch.lineOwned')
      : t('purchaseOrderDetail.invoiceMatch.failed');

  const handleConfirmMatchInvoice = (data: { invoiceNumber: string; invoiceDate: string; invoiceTotalCents: number }) => {
    if (!selectedPO) return;
    matchInvoiceMutation.mutate(
      { id: selectedPO.id, data },
      {
        onSuccess: (result) => {
          notify.success(
            result.isExactMatch
              ? t('purchaseOrderDetail.invoiceMatch.successExact')
              : t('purchaseOrderDetail.invoiceMatch.successDiscrepancy', {
                  amount: (result.discrepancyCents / 100).toFixed(2),
                }),
            selectedPO.orderNumber,
          );
          setMatchInvoiceDialogOpen(false);
        },
        onError: (error) => notify.error(tc('error'), matchInvoiceFailureText(error)),
      },
    );
  };

  const handleCloseMatchInvoiceDialog = () => {
    setMatchInvoiceDialogOpen(false);
  };

  /**
   * Cancelling a purchase order, behind a confirmation (#2722).
   *
   * Two problems, both fixed here. The red menu item fired a server status
   * change on the first click with no ConfirmDialog — and it was labelled
   * "Poista" while the effect is a transition to `cancelled`, so the label
   * promised something different from what happened.
   */
  const handleRequestCancel = (po: PurchaseOrder) => {
    setCancelTarget(po);
    handleMenuClose();
  };

  const handleConfirmCancel = () => {
    const po = cancelTarget;
    if (!po) return;
    updateStatusMutation.mutate(
      { id: po.id, status: 'cancelled' },
      {
        onSuccess: () => notify.success(t('purchaseOrders.orderCancelled'), `${po.orderNumber}`),
        onError: () => notify.error(tc('error'), t('purchaseOrders.cancelFailed')),
      },
    );
    setCancelTarget(null);
  };

  /**
   * Export exactly the rows on screen (#2722).
   *
   * Was a lone `notify.info` promising "Ostotilaukset viedään
   * CSV-tiedostoon..." while producing nothing. Exports the filtered list
   * rather than everything, so what lands in the file is what the user is
   * looking at.
   */
  const handleExport = () => {
    if (filteredOrders.length === 0) {
      notify.warning(t('purchaseOrders.exportEmpty'), t('purchaseOrders.exportEmptyDesc'));
      return;
    }
    downloadCsv(
      `${t('purchaseOrders.exportFilename')}-${new Date().toISOString().slice(0, 10)}`,
      [
        t('purchaseOrders.colOrderNumber'),
        t('purchaseOrders.colSupplier'),
        t('purchaseOrders.colStatus'),
        t('purchaseOrders.colTotal'),
        t('purchaseOrders.colCreated'),
      ],
      filteredOrders.map((po) => [
        po.orderNumber,
        po.supplier?.name ?? '',
        statusLabels[po.status] ?? po.status,
        po.totalCents / 100,
        po.orderedAt ?? po.createdAt ?? '',
      ]),
    );
    notify.success(
      t('purchaseOrders.exportReady'),
      t('purchaseOrders.exportReadyDesc', { count: filteredOrders.length }),
    );
  };

  const handleSaveForm = (data: PurchaseOrderFormData, submitForApproval: boolean) => {
    const lines = data.items.map((item) => ({
      partId: item.inventoryItemId ?? '',
      quantityOrdered: item.quantity,
      unitPriceCents: item.unitCostCents,
      ...(item.linkedBookingId ? { linkedBookingId: item.linkedBookingId } : {}),
    }));

    // Editing a draft only ever updates its lines/details — "submit for
    // approval" has no separate meaning here beyond what create already does
    // (see below), since there's no distinct "pending approval" state to move
    // a draft into via this dialog.
    if (selectedPO?.status === 'draft') {
      updateMutation.mutate(
        {
          id: selectedPO.id,
          data: {
            supplierId: data.supplierId,
            warehouseId: data.warehouseId,
            notes: data.notes ?? null,
            shippingCents: data.shippingCents,
            lines,
          },
        },
        {
          onSuccess: () => notify.success(t('purchaseOrders.orderUpdated'), selectedPO.orderNumber),
          onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('purchaseOrders.updateFailed') })),
        },
      );
      return;
    }

    createMutation.mutate(
      {
        supplierId: data.supplierId,
        warehouseId: data.warehouseId,
        notes: data.notes,
        shippingCents: data.shippingCents,
        lines,
      },
      {
        onSuccess: (result) => {
          const statusText = submitForApproval ? t('purchaseOrders.sentForApproval') : t('purchaseOrders.asDraft');
          notify.success(t('purchaseOrders.orderCreated'), statusText);
          // "Submit for approval" creates the draft, then immediately
          // transitions it to approved — the backend has no separate
          // "pending" state to submit into.
          if (submitForApproval) {
            updateStatusMutation.mutate({ id: result.id, status: 'approved' });
          }
        },
        onError: () => notify.error(tc('error'), t('purchaseOrders.createFailed')),
      },
    );
  };

  const handleCloseDetailModal = () => {
    closedPoId.current = poParam;
    keepPoSelection.current = false;
    setDetailModalOpen(false);
    setSelectedPO(null);
    writePoParam(null);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    if (!formDialogOpen) {
      setSelectedPO(null);
    }
  };

  const handleEditFromDetail = (po: PurchaseOrder) => {
    keepPoSelection.current = true;
    closedPoId.current = poParam;
    setDetailModalOpen(false);
    setSelectedPO(po);
    setFormDialogOpen(true);
    writePoParam(null);
  };

  const handleApproveFromDetail = (po: PurchaseOrder) => {
    handleApprove(po);
    closedPoId.current = poParam;
    keepPoSelection.current = false;
    setDetailModalOpen(false);
    writePoParam(null);
  };

  const handleMarkOrderedFromDetail = (po: PurchaseOrder) => {
    // The detail stays open: it shows the order's sending next to a refusal, and the new status once the server has moved it.
    handleMarkOrdered(po);
  };

  const handleMatchInvoiceFromDetail = (po: PurchaseOrder) => {
    keepPoSelection.current = true;
    closedPoId.current = poParam;
    handleMatchInvoice(po);
    setDetailModalOpen(false);
    writePoParam(null);
  };

  const handlePrint = (po: PurchaseOrder) => {
    notify.info(t('purchaseOrders.printing'), `${po.orderNumber}`);
  };

  const poSortCompareFn = (a: PurchaseOrder, b: PurchaseOrder, key: string): number => {
    switch (key) {
      case 'orderNumber':
        return a.orderNumber.localeCompare(b.orderNumber);
      case 'supplier':
        return a.supplier.name.localeCompare(b.supplier.name, 'fi');
      case 'total':
        return a.totalCents - b.totalCents;
      case 'created':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'deliveryDate': {
        const dateA = a.expectedDeliveryDate ? new Date(a.expectedDeliveryDate).getTime() : 0;
        const dateB = b.expectedDeliveryDate ? new Date(b.expectedDeliveryDate).getTime() : 0;
        return dateA - dateB;
      }
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      header: t('purchaseOrders.colOrderNumber'),
      sortKey: 'orderNumber',
      renderCell: (po) => (
        <>
          <Typography variant="body2" fontWeight={600}>
            {po.orderNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {po.createdByName}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colSupplier'),
      sortKey: 'supplier',
      renderCell: (po) => (
        <>
          <Typography variant="body2">{po.supplier.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.deliveryDays', { days: po.supplier.deliveryDays })}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colParts'),
      renderCell: (po) => (
        <>
          <Typography variant="body2">{t('purchaseOrders.partsCount', { count: po.items.length })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.totalPcs', { count: po.items.reduce((sum, item) => sum + item.quantity, 0) })}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colTotal'),
      sortKey: 'total',
      align: 'right',
      renderCell: (po) => (
        <>
          <Typography variant="body2" fontWeight={600}>
            €{(po.totalCents / 100).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.vatIncluded')}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colStatus'),
      renderCell: (po) => (
        <Chip label={statusLabels[po.status]} size="small" color={statusColors[po.status]} />
      ),
    },
    {
      header: t('purchaseOrders.colCreated'),
      sortKey: 'created',
      renderCell: (po) => (
        <>
          <Typography variant="body2">
            {format(new Date(po.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(po.createdAt), 'HH:mm')}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colDeliveryDate'),
      sortKey: 'deliveryDate',
      renderCell: (po) =>
        po.expectedDeliveryDate ? (
          <>
            <Typography variant="body2">
              {format(new Date(po.expectedDeliveryDate), 'd.M.yyyy', { locale: dateFnsLocale() })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('purchaseOrders.estimated')}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (po) => (
        <Tooltip title={tc('moreOptions')}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, po); }} aria-label={tc('moreOptions')}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('purchaseOrders.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('purchaseOrders.subtitle')}
          </Typography>
        </Box>
        {!isMobile && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
              {t('purchaseOrders.export')}
            </Button>
            {canCreatePurchaseOrder && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                {t('purchaseOrders.newOrder')}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <Card>
        {/* Search */}
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            placeholder={t('purchaseOrders.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Quick Filters (replaces Tabs) */}
        <Box sx={{ px: 2, pt: 2 }}>
          <DataTable<PurchaseOrder>
            columns={columns}
            data={filteredOrders}
            getRowKey={(po) => po.id}
            sortCompareFn={poSortCompareFn}
            defaultSortKey="created"
            defaultSortDirection="desc"
            onRowClick={handleView}
            getRowHref={purchaseOrderHref}
            quickFilters={quickFilters}
            activeFilter={tab}
            onFilterChange={(value) => setTab(isTabValue(value) ? value : 'all')}
            paperWrapper={false}
            emptyMessage={searchQuery ? t('purchaseOrders.noResults') : t('purchaseOrders.noOrders')}
            // No guidance when a search found nothing — the user knows why.
            emptyDescription={searchQuery ? undefined : t('purchaseOrders.noOrdersDesc')}
            renderMobileCard={(po) => (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {po.orderNumber}
                  </Typography>
                  <Chip label={statusLabels[po.status]} size="small" color={statusColors[po.status]} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {po.supplier.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    €{(po.totalCents / 100).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(po.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuPO && handleView(menuPO)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {t('purchaseOrders.viewDetails')}
        </MenuItem>
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleEdit(menuPO)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {tc('edit')}
          </MenuItem>
        )}
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleApprove(menuPO)}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.approve')}
          </MenuItem>
        )}
        {menuPO?.status === 'approved' && (
          <MenuItem onClick={() => menuPO && handleMarkOrdered(menuPO)}>
            <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.markOrdered')}
          </MenuItem>
        )}
        {canReceiveGoods && (menuPO?.status === 'ordered' || menuPO?.status === 'partially_received') && (
          <MenuItem onClick={() => menuPO && handleOpenReceiving(menuPO)}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.receiveGoods')}
          </MenuItem>
        )}
        {canOpenInvoices && (menuPO?.status === 'partially_received' || menuPO?.status === 'received') && (
          <MenuItem onClick={() => menuPO && handleView(menuPO)}>
            <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.manageInvoices')}
          </MenuItem>
        )}
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleRequestCancel(menuPO)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {/* "Peruuta tilaus", not "Poista": the row is not removed, its
                status becomes cancelled (#2722). */}
            {t('purchaseOrders.cancelOrder')}
          </MenuItem>
        )}
      </Menu>

      {/* Detail Modal */}
      <PurchaseOrderDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        purchaseOrder={selectedPOWithDetail}
        detailState={detailState}
        onRetryDetail={() => void poDetailQuery.refetch()}
        procurementEnabled={procurementEnabled}
        onEdit={handleEditFromDetail}
        onApprove={handleApproveFromDetail}
        onMarkOrdered={handleMarkOrderedFromDetail}
        onPrint={handlePrint}
        onMatchInvoice={handleMatchInvoiceFromDetail}
      />

      {/* Form Dialog */}
      <PurchaseOrderFormDialog
        open={formDialogOpen && isFormDataReady}
        onClose={handleCloseFormDialog}
        purchaseOrder={selectedPOWithDetail}
        suppliers={suppliersList}
        inventory={inventory}
        warehouses={warehousesList}
        onSave={handleSaveForm}
      />

      {/* Match Invoice Dialog */}
      <MatchInvoiceDialog
        open={matchInvoiceDialogOpen}
        onClose={handleCloseMatchInvoiceDialog}
        orderNumber={selectedPO?.orderNumber ?? ''}
        poTotalCents={selectedPO?.totalCents ?? 0}
        onConfirm={handleConfirmMatchInvoice}
        isLoading={matchInvoiceMutation.isPending}
      />

      {/* Mobile FAB */}
      {isMobile && canCreatePurchaseOrder && (
        <Fab
          color="primary"
          size="medium"
          onClick={handleCreate}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1050 }}
          aria-label={t('purchaseOrders.newOrder')}
        >
          <AddIcon />
        </Fab>
      )}

      {/*
        The label says "Peruuta tilaus" and the effect is a cancel — they
        used to disagree, and there was no confirmation at all (#2722).
      */}
      <ConfirmDialog
        open={cancelTarget !== null}
        title={t('purchaseOrders.cancelOrderTitle')}
        message={t('purchaseOrders.cancelOrderConfirm', { orderNumber: cancelTarget?.orderNumber ?? '' })}
        confirmText={t('purchaseOrders.cancelOrder')}
        confirmColor="error"
        onConfirm={handleConfirmCancel}
        onCancel={() => { setCancelTarget(null); }}
      />
    </Box>
  );
}
